import { api, ApiError, mutate, mutateUpload, postNow, queuePersistenceOk, readCache } from './api'
import { storeAppParam } from './store-app'
import { buzz } from './haptic'
import { summariseRound, type RoundCustomer } from './attention'
import { getMe, worksAlone } from './me'
import { clear, el, PRIVACY_URL, toast } from './ui'
import { helpWords, sentWords } from './help-words'

// Help (Liam, 2026-09-24: "yes lets build this next"). A Help button in the
// top bar opens a panel: type a question in your own words and Roundly shows
// one of the answers Liam approved (docs/help/library.json), with a button to
// the right screen. Jev picks which answer (functions/api/help/ask.ts); it
// cannot write anything of its own. Not sure: "Is it one of these?". No
// answer: "Send this to Liam" (support phase 1: a reply-by time, an email to Liam), which lands on the admin's list of answers to
// write next. Owners also get a "Getting started" list until it is all done.
// Desktop: a panel on the right. Phone: a sheet from the bottom.

interface HelpAnswer {
  id: string
  who: 'owner' | 'worker' | 'both'
  title: string
  answer: string[]
  link: { href: string; label: string } | null
  video?: string
  video_phone?: string
}

interface AskReply {
  id: number
  outcome: 'answered' | 'unsure' | 'none' | 'error' | 'capped' | 'no_key'
  answer_id?: string
  suggestions?: string[]
}

const COMMON: Record<'owner' | 'worker', string[]> = {
  owner: ['book-visit', 'add-worker', 'hide-prices', 'chase-payment', 'rebook'],
  worker: ['start-finish', 'worker-payment', 'couldnt-do', 'running-late', 'voice-note'],
}
// A one-person round (Liam, 19 Oct 2026): nobody to add as a worker or hide prices from, so its owner's common questions
// have setting their own days and the days Messages offers in place of those two.
const COMMON_ALONE = ['book-visit', 'owner-working-days', 'message-dates', 'chase-payment', 'rebook']
// Answers only about working with others (who is out on a day, a worker's phone, prices hidden from a worker, worker
// notes and pay). A one-person round is never offered them: not in the common questions, "Is it one of these?" or as
// the answer to a typed question. Client side only: the server keeps every answer for every round.
const TEAM_ONLY = new Set(['worker-day', 'owner-does-jobs', 'hide-prices', 'worker-missing-jobs', 'owner-notes', 'worker-pay-invoices'])

let open: HTMLDialogElement | null = null

// Inside Roundly's iPhone app the server leaves out the answers about paying for Roundly (Apple 3.1.3).
const IN_APP = storeAppParam()

// Fresh each time the panel opens, so a newly written answer is there; with
// no signal, the copy this phone saved last time (Grok review 2026-09-25).
async function loadAnswers(): Promise<HelpAnswer[]> {
  const shown = (list: HelpAnswer[]): HelpAnswer[] => worksAlone() ? list.filter((a) => !TEAM_ONLY.has(a.id)) : list
  try {
    return shown((await api<{ answers: HelpAnswer[] }>(`/api/help${IN_APP}`, 'help')).answers)
  } catch (err) {
    const saved = readCache<{ answers: HelpAnswer[] }>('help')
    if (saved) return shown(saved.data.answers)
    throw err
  }
}

export function bindHelpButton(): void {
  const button = document.querySelector<HTMLButtonElement>('#help-button')
  const role = getMe()?.role
  if (!button || (role !== 'owner' && role !== 'worker')) return
  button.hidden = false
  button.addEventListener('click', () => openHelp(role))
}

function openHelp(role: 'owner' | 'worker'): void {
  if (open) return
  const dialog = el('dialog', { class: 'help-panel', 'aria-label': 'Help' })
  const close = el('button', { type: 'button', class: 'help-close', 'aria-label': 'Close Help' }, '×')
  const head = el('div', { class: 'help-head' }, el('h2', {}, 'Help'), close)
  const input = el('input', {
    type: 'text',
    id: 'help-question',
    maxlength: '300',
    autocomplete: 'off',
    'aria-label': 'Ask a question',
    placeholder: 'Type your question',
  })
  const ask = el('button', { type: 'submit', class: 'btn-primary' }, 'Ask')
  const form = el('form', { class: 'help-ask' }, input, ask)
  // What is typed goes to Jev (outside Roundly) and onto Roundly's list of
  // questions to answer, so it should never carry a customer's details.
  const privacy = el('p', { class: 'help-privacy' }, "Please don't include a customer's name, address or codes. ", el('a', { href: PRIVACY_URL, target: '_blank', rel: 'noopener' }, 'Privacy'))
  const result = el('div', { class: 'help-result', 'aria-live': 'polite' })
  const home = el('div', { class: 'help-home' })
  const body = el('div', { class: 'help-body' }, form, privacy, result, home)
  let commonShown = false
  const offlineNote = (): string =>
    commonShown ? 'Help needs signal to answer a question. The common questions below still work.' : 'Help needs signal to answer a question.'
  dialog.append(head, body)
  document.body.append(dialog)
  open = dialog

  const shut = (): void => {
    if (dialog.open) dialog.close()
  }
  dialog.addEventListener('close', () => {
    dialog.remove()
    if (open === dialog) open = null
  })
  close.addEventListener('click', shut)
  // A tap outside the panel closes it (the backdrop is the dialog itself).
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) shut()
  })
  window.addEventListener('hashchange', shut, { once: true })
  dialog.showModal()

  const showAnswer = (a: HelpAnswer, questionId: number | null): void => {
    clear(result)
    const card = el('div', { class: 'help-answer' }, el('h3', {}, helpWords(a.title)))
    const steps = el('ol', {})
    for (const s of a.answer) steps.append(el('li', {}, helpWords(s)))
    card.append(steps)
    // A short phone recording of the steps, under them (Liam, 28 Oct 2026: "show me a recording so we can show it him ...
    // put it in the help section"). Plays inline on a phone, no sound; nothing loads until it is played.
    // On a phone, the phone's recording where there is one (Liam, 28 Oct 2026: "the video is for mobile but we asked
    // question on desktop"): the computer's otherwise.
    const video = window.matchMedia?.('(max-width: 760px)').matches && a.video_phone ? a.video_phone : a.video
    if (video) {
      card.append(el('figure', { class: `help-video${video === a.video_phone ? ' help-video-phone' : ''}` },
        el('video', { src: video, poster: video.replace(/\.mp4$/, '.jpg'), controls: '', playsinline: '', muted: '', preload: 'none', 'aria-label': `Video: ${a.title}` }),
        el('figcaption', { class: 'muted' }, 'Watch how')))
    }
    if (a.link) {
      const go = el('a', { class: 'help-go', href: a.link.href }, `${a.link.label} →`)
      go.addEventListener('click', () => shut())
      card.append(go)
    }
    if (questionId) card.append(feedback(questionId))
    // An answer to a typed question was picked by Roundly's assistant from Liam's written answers:
    // said plainly, so nobody takes it for Liam himself (plan Step 2, support phase 1).
    if (questionId) result.append(el('p', { class: 'help-from' }, "Roundly's assistant"))
    result.append(card)
  }

  const feedback = (questionId: number): HTMLElement => {
    const yes = el('button', { type: 'button' }, 'Yes')
    const no = el('button', { type: 'button' }, 'No')
    const row = el('div', { class: 'help-helped' }, el('span', {}, 'Did this answer it?'), yes, no)
    yes.addEventListener('click', () => {
      send(questionId, { helpful: true })
      clear(row)
      row.append(el('span', {}, 'Thanks.'))
    })
    no.addEventListener('click', () => {
      send(questionId, { helpful: false })
      clear(row)
      row.append(sendToUs(questionId, "Sorry about that. Send your question to Liam, who built Roundly, and he'll reply."))
    })
    return row
  }

  // "Send this to Liam" (support phase 1): it says Sent only once the question has reached Roundly,
  // with the time Liam will reply by, worked out there (two working hours, 9 to 5 UK time).
  // More detail and screenshots (0067; Liam, 12 Oct 2026, on the drawing: "the details button needs a little extra
  // umph just a little as its lost maybe the button should just say send and then green animation of some kind when
  // sent?"): "+ Add more detail or a screenshot" opens a box for words and up to 3 screenshots, kept on this phone
  // until Send. Send puts the screenshots with the question first, then sends it with the words, so Liam's email has
  // both; the button turns green with a tick once it has reached Roundly.
  const sendToUs = (questionId: number, text: string): HTMLElement => {
    const box = el('div', { class: 'help-sendbox' }, el('p', {}, text))
    const more = el('button', { type: 'button', class: 'help-more', 'aria-expanded': 'false' },
      el('span', { class: 'help-more-plus', 'aria-hidden': 'true' }, '+'), el('span', { class: 'help-more-words' }, 'Add more detail or a screenshot'))
    const extra = el('div', { class: 'help-extra' })
    extra.hidden = true
    const detail = el('textarea', { class: 'help-detail', rows: '3', maxlength: '2000', 'aria-label': 'More detail for Liam', placeholder: 'What happened, and what you expected' }) as HTMLTextAreaElement
    const shotRow = el('div', { class: 'help-shots' })
    const pick = el('input', { type: 'file', accept: 'image/*', multiple: '', class: 'help-shot-input', 'aria-hidden': 'true', tabindex: '-1' }) as HTMLInputElement
    const addShot = el('button', { type: 'button', class: 'btn-secondary help-shot-add' }, 'Add a screenshot')
    const shotNote = el('p', { class: 'help-shot-note' }, "Up to 3 pictures. Please leave out customers' names and addresses.")
    extra.append(detail, shotRow, shotNote, pick)
    // Each picture once: its key and bytes stay the same if Send is tapped again after lost signal.
    const shots: { blob: Blob; url: string; key: string; kept: boolean }[] = []
    let busy = false
    // Pictures still being made smaller: Send waits for them, so none is left behind (review).
    let preparing = 0
    const sendReady = (): void => { btn.disabled = busy || preparing > 0 }
    const drawShots = (): void => {
      clear(shotRow)
      shots.forEach((sh, i) => {
        const x = el('button', { type: 'button', class: 'help-shot-x', 'aria-label': `Remove screenshot ${i + 1}` }, '×') as HTMLButtonElement
        x.disabled = busy || sh.kept
        x.addEventListener('click', () => { URL.revokeObjectURL(sh.url); shots.splice(i, 1); drawShots() })
        shotRow.append(el('div', { class: 'help-shot' }, el('img', { src: sh.url, alt: `Screenshot ${i + 1}` }), x))
      })
      addShot.disabled = busy
      if (shots.length < 3) shotRow.append(addShot)
    }
    more.addEventListener('click', () => {
      more.hidden = true
      more.setAttribute('aria-expanded', 'true')
      extra.hidden = false
      drawShots()
      detail.focus({ preventScroll: false })
    })
    addShot.addEventListener('click', () => pick.click())
    pick.addEventListener('change', () => {
      const files = Array.from(pick.files ?? []).slice(0, 3 - shots.length)
      pick.value = ''
      preparing += files.length
      sendReady()
      void Promise.all(files.map(shrinkShot)).then((blobs) => {
        preparing -= files.length
        sendReady()
        let bad = 0
        for (const blob of blobs) {
          if (!blob || shots.length >= 3) { bad += blob ? 0 : 1; continue }
          shots.push({ blob, url: URL.createObjectURL(blob), key: crypto.randomUUID(), kept: false })
        }
        said.textContent = bad ? "That picture couldn't be added. Try a screenshot instead." : ''
        if (bad && !said.isConnected) box.append(said)
        drawShots()
      })
    })
    const words = el('span', { class: 'help-send-words' }, 'Send')
    const btn = el('button', { type: 'button', class: 'btn-primary help-send' }, words) as HTMLButtonElement
    const said = el('p', { class: 'help-sent', 'aria-live': 'polite' })
    const lock = (on: boolean): void => {
      busy = on
      sendReady()
      detail.readOnly = on
      if (!extra.hidden) drawShots()
    }
    // Green, with a tick drawn in, then the reply-by words under it.
    const sentLook = (line: string): void => {
      btn.classList.add('is-sent')
      clear(btn)
      btn.append(el('span', { class: 'help-tick', 'aria-hidden': 'true' }), el('span', { class: 'help-send-words' }, 'Sent'))
      buzz('success')
      more.hidden = true
      extra.hidden = true
      for (const sh of shots) URL.revokeObjectURL(sh.url)
      said.textContent = line
      if (!said.isConnected) box.append(said)
    }
    btn.addEventListener('click', () => {
      if (busy || preparing > 0 || btn.classList.contains('is-sent')) return
      lock(true)
      words.textContent = 'Sending…'
      said.textContent = ''
      void (async () => {
        buzz('light')
        for (const sh of shots) {
          if (sh.kept) continue
          const up = await mutateUpload({ path: `/api/help/questions/${questionId}/screenshots`, key: sh.key, fields: {}, file: sh.blob, filename: 'screenshot.jpg', fileField: 'file', ackField: 'row' })
          if (up.ok) { sh.kept = true; continue }
          // Help was closed while it was sending (review: the question was lost): it still goes, with the words and
          // the pictures that made it, and a note says so.
          if (!box.isConnected) break
          lock(false)
          words.textContent = 'Send'
          said.textContent = up.status === 0
            ? 'No signal, so the screenshots could not go yet. Tap Send again when you have signal.'
            : `A screenshot didn't send (${up.error ?? 'try again'}). Remove it, or tap Send again.`
          if (!said.isConnected) box.append(said)
          return
        }
        const ack = await sendToLiam(questionId, detail.value)
        if (!box.isConnected) {
          // No signal or a fault at Roundly: the question is kept and sent by itself, so never "try again" (that would
          // send Liam the same question twice). Screenshots that did not go are said, whatever happened (review).
          const without = shots.some((sh) => !sh.kept) ? ", without the screenshots that didn't send" : ''
          toast(ack.ok ? `Your question went to Liam${without}.`
            : ack.status === 0 ? `No signal. Your question goes to Liam when you have signal${without}.`
            : ack.status >= 500 ? `Your question is waiting to go to Liam. Roundly keeps trying${without}.`
            : "Your question didn't send. Open Help and try again.")
          return
        }
        if (ack.ok) {
          const row = ack.row as { reply_by?: string | null } | undefined
          lock(false)
          btn.disabled = true
          sentLook(sentWords(row?.reply_by ?? null))
          return
        }
        if (ack.status === 0) {
          // Only say "saved on this phone" when it really is (storage can refuse); otherwise it waits
          // in this open app (Codex review).
          lock(false)
          btn.disabled = true
          words.textContent = 'Waiting for signal'
          more.hidden = true
          extra.hidden = true
          said.textContent = queuePersistenceOk()
            ? "No signal, so it's saved on this phone. It goes to Liam when you have signal, and he'll email you back within two working hours of it arriving."
            : 'No signal. Keep Roundly open and it goes to Liam when the signal comes back.'
          if (!said.isConnected) box.append(said)
          return
        }
        lock(false)
        words.textContent = 'Send'
        said.textContent = "Couldn't send that. Try again."
        if (!said.isConnected) box.append(said)
      })()
    })
    box.append(more, extra, btn)
    return box
  }

  const noAnswer = (reply: AskReply): void => {
    clear(result)
    const trouble = reply.outcome === 'error' || reply.outcome === 'no_key' || reply.outcome === 'capped'
    const box = el('div', { class: 'help-none' },
      el('h3', {}, trouble ? "Help can't look that up right now" : "We don't have an answer for that yet"))
    box.append(sendToUs(reply.id, trouble
      ? 'Try the common questions below, or send it to Liam, who built Roundly.'
      : "Send it to Liam, who built Roundly, and he'll reply. The answer is added here for next time."))
    result.append(box)
  }

  const unsure = (reply: AskReply, list: HelpAnswer[]): void => {
    // A one-person round whose every suggestion was about working with others: nothing to pick from, so it goes to Liam.
    if (worksAlone() && !(reply.suggestions ?? []).some((id) => list.some((x) => x.id === id))) return noAnswer(reply)
    clear(result)
    const box = el('div', { class: 'help-unsure' }, el('h3', {}, 'Is it one of these?'))
    for (const id of reply.suggestions ?? []) {
      const a = list.find((x) => x.id === id)
      if (!a) continue
      const pick = el('button', { type: 'button', class: 'help-pick' }, a.title)
      pick.addEventListener('click', () => showAnswer(a, reply.id))
      box.append(pick)
    }
    const none = el('button', { type: 'button', class: 'help-pick help-pick-none' }, 'None of these')
    none.addEventListener('click', () => none.replaceWith(sendToUs(reply.id, "Send your question to Liam, who built Roundly, and he'll reply.")))
    box.append(none)
    result.append(box)
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    const question = input.value.trim()
    if (question.length < 2) {
      input.focus()
      return
    }
    clear(result)
    if (!navigator.onLine) {
      result.append(el('p', { class: 'help-note' }, offlineNote()))
      return
    }
    ask.disabled = true
    result.append(el('p', { class: 'help-note' }, 'Looking…'))
    void Promise.all([postNow<AskReply>(`/api/help/ask${IN_APP}`, { question }), loadAnswers()])
      .then(([reply, list]) => {
        if (reply.outcome === 'answered') {
          const a = list.find((x) => x.id === reply.answer_id)
          if (a) return showAnswer(a, reply.id)
        }
        if (reply.outcome === 'unsure' && reply.suggestions?.length) return unsure(reply, list)
        return noAnswer(reply)
      })
      .catch((err: unknown) => {
        clear(result)
        const offline = err instanceof ApiError && err.status === 0
        result.append(el('p', { class: 'help-note' }, offline
          ? offlineNote()
          : commonShown ? "Help can't look that up right now. Try the common questions below." : "Help can't look that up right now."))
      })
      .finally(() => {
        ask.disabled = false
      })
  })

  // Common questions (and, for owners, the setup list) below the box.
  void loadAnswers()
    .then((list) => {
      if (role === 'owner') void gettingStarted(home)
      const chips = el('div', { class: 'help-chips' })
      for (const id of role === 'owner' && worksAlone() ? COMMON_ALONE : COMMON[role]) {
        const a = list.find((x) => x.id === id)
        if (!a) continue
        const chip = el('button', { type: 'button', class: 'help-chip' }, a.title)
        chip.addEventListener('click', () => showAnswer(a, null))
        chips.append(chip)
      }
      home.append(el('section', { class: 'help-common' }, el('h3', {}, 'Common questions'), chips))
      commonShown = true
    })
    .catch(() => {
      home.append(el('p', { class: 'help-note' }, "Couldn't load Help right now. Check your signal and try again."))
    })
  // Straight into the question box on a computer. Not on a phone (Liam's
  // iPhone, 26 Sep): the keyboard would jump up and hide the Getting started
  // list and the common questions before anyone has chosen to type.
  if (typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    window.setTimeout(() => input.focus(), 50)
  }
}

// The question to Liam, with anything they added (0067) in the same save.
function sendToLiam(questionId: number, detail = '') {
  const words = detail.trim()
  return mutate({ label: 'Help question sent to Liam', method: 'PATCH', path: `/api/help/questions/${questionId}`, body: { sent_to_us: true, ...(words ? { detail: words } : {}) } })
}

// A screenshot made smaller for poor signal: up to 2000 pixels on its longest side, as a JPEG, still sharp enough to
// read. Null when the phone cannot read the picture.
async function shrinkShot(file: File): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(file)
    const k = Math.min(1, 2000 / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * k))
    canvas.height = Math.max(1, Math.round(bitmap.height * k))
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.86))
  } catch {
    return null
  }
}

function send(questionId: number, body: { helpful?: boolean; sent_to_us?: true }): void {
  void mutate({
    label: body.sent_to_us ? 'Help question sent to Roundly' : 'Help: did this answer it',
    method: 'PATCH',
    path: `/api/help/questions/${questionId}`,
    body,
  })
}

// "Getting started", for owners, from what the round already holds. The
// whole list hides once every step is done. Any read failing hides it too:
// a half-known list would tick the wrong things.
interface Step {
  text: string
  done: boolean
  detail?: string
  link?: { href: string; label: string }
}

async function gettingStarted(into: HTMLElement): Promise<void> {
  let customers: RoundCustomer[]
  let rosteredAhead: number
  try {
    const [c, r] = await Promise.all([
      api<{ customers: RoundCustomer[] }>('/api/customers', 'customers'),
      api<{ round_days: unknown[] }>('/api/round-days'),
    ])
    customers = c.customers
    rosteredAhead = r.round_days.length
  } catch {
    return
  }
  const me = getMe()
  // Active customers only: an old paused or lapsed row with no price must not
  // hold the list open for good (Grok review 2026-09-25).
  const active = customers.filter((c) => c.status === 'active')
  const missingDetails = summariseRound(active).details.length
  const steps: Step[] = [
    // Named at sign-up, so it starts ticked and the list never starts at nothing (Liam, 29 Sep); bank details are
    // their own step.
    { text: 'Name your business', done: !!(me?.settings?.business_name?.trim() || me?.businessName?.trim() || me?.business?.name?.trim()), link: { href: '#/business', label: 'Open Your business' } },
    { text: 'Add your bank details', done: !!me?.settings?.bank_lines?.trim(), link: { href: '#/business', label: 'Open Your business' } },
    {
      text: 'Add your customers',
      done: customers.length > 0,
      detail: customers.length ? `${customers.length} on the round` : undefined,
      link: { href: '#/grid', label: 'Open Work' },
    },
    {
      text: 'Give every customer a price and a cycle',
      done: active.length > 0 && missingDetails === 0,
      detail: missingDetails ? `${missingDetails} still need one` : undefined,
      link: { href: '#/grid?view=details&status=all', label: 'Open Work' },
    },
    // Someone on a day (a worker, or the owner themself): what lets Messages
    // offer days. Adding a worker login is not a step: plenty of rounds are
    // one person, and the list must be able to finish (Grok review). A one-person round sets its days only in My working
    // days (Liam, 19 Oct 2026).
    worksAlone()
      ? { text: 'Set the days you work', done: rosteredAhead > 0, link: { href: '#/my-days', label: 'Open My working days' } }
      : { text: 'Put who is working on a day in the diary', done: rosteredAhead > 0, link: { href: '#/schedule', label: 'Open the diary' } },
    // A visit someone agreed, not the app's own suggestion or reminder.
    { text: 'Book and agree your first visits', done: customers.some((c) => !!c.next_confirmed), link: { href: '#/schedule', label: 'Open the diary' } },
  ]
  const doneCount = steps.filter((s) => s.done).length
  if (doneCount === steps.length || !into.isConnected) return
  const list = el('ul', {})
  for (const s of steps) {
    const li = el('li', { class: s.done ? 'help-step-done' : 'help-step' })
    const label = el('span', {}, s.text)
    if (s.detail) label.append(el('small', {}, s.detail))
    li.append(label)
    if (!s.done && s.link) {
      const go = el('a', { href: s.link.href }, s.link.label)
      go.addEventListener('click', () => open?.close())
      li.append(go)
    }
    list.append(li)
  }
  const bar = el('div', { class: 'help-bar', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': String(steps.length), 'aria-valuenow': String(doneCount) }, el('i', { style: `width:${Math.round((doneCount / steps.length) * 100)}%` }))
  into.prepend(el('section', { class: 'help-setup' },
    el('div', { class: 'help-setup-head' }, el('h3', {}, 'Getting started'), el('span', {}, `${doneCount} of ${steps.length} done`)),
    bar, list))
}
